import React, { useState, useEffect } from 'react';

const JewelleryFilterBar = ({ filters, setFilters, isAdmin }) => {
  // Local state for debounced text search
  const [searchTerm, setSearchTerm] = useState(filters.name || filters.search || '');

  // Predefined Categories
  const CATEGORIES = ['Ring', 'Necklace', 'Earring', 'Bangle', 'Chain', 'Set', 'Bracelet', 'Mangalsutra', 'Pendant', 'Coin', 'Article'];

  // Sync local state if filters change externally
  useEffect(() => {
    if ((filters.name || filters.search) !== searchTerm) {
      setSearchTerm(filters.name || filters.search || '');
    }
  }, [filters.name, filters.search]);

  // Debounce search update (only for plain text if no smart enter pressed)
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, name: searchTerm }));
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, setFilters]);

  // --- SMART SEARCH PARSER ---
  const handleSmartSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = searchTerm.toLowerCase();
      let newFilters = { ...filters };
      let remainingText = query;

      // 1. Detect Price: "under 50k", "below 20000"
      const priceMatch = remainingText.match(/(?:under|below|less than)\s*(\d+(?:k|000)?|1\s*lakh)/i);
      if (priceMatch) {
        let rawVal = priceMatch[1];
        let priceLimit = 0;
        if (rawVal.includes('lakh')) priceLimit = 100000;
        else if (rawVal.includes('k')) priceLimit = parseInt(rawVal) * 1000;
        else priceLimit = parseInt(rawVal);

        newFilters.maxPrice = priceLimit;
        newFilters.minPrice = ''; // Reset min if "under" is used
        remainingText = remainingText.replace(priceMatch[0], '').trim();
      }

      // 2. Detect Type: "Gold", "Silver", "Diamond"
      const types = ['gold', 'silver', 'diamond', 'custom'];
      let foundTypes = [...(Array.isArray(newFilters.type) ? newFilters.type : (newFilters.type ? newFilters.type.split(',') : []))];

      types.forEach(t => {
        if (remainingText.includes(t)) {
          if (!foundTypes.includes(t)) foundTypes.push(t);
          remainingText = remainingText.replace(t, '').trim();
        }
      });
      if (foundTypes.length > 0) newFilters.type = foundTypes;

      // 3. Detect Category
      CATEGORIES.forEach(cat => {
        if (remainingText.includes(cat.toLowerCase())) {
          newFilters.category = cat; // Set specific category
          remainingText = remainingText.replace(cat.toLowerCase(), '').trim();
        }
      });

      // 4. Detect Purity: "22k", "18k"
      const purityMatch = remainingText.match(/(\d{2})\s*k/i);
      if (purityMatch) {
        newFilters.purity = purityMatch[1] + 'K';
        remainingText = remainingText.replace(purityMatch[0], '').trim();
      }

      // 5. Cleanup and Update
      newFilters.name = remainingText.replace(/\s+/g, ' ').trim(); // Remaining text is the "Name" or "Tag" part
      setFilters(newFilters);
      setSearchTerm(newFilters.name); // Update input to show only the remaining specific query (optional, or keep full?)
      // Let's clear the specific terms from input to show they were "consumed" into filters?
      // Or keep it? Users usually like "Magic". Let's update the input to reflect the "Name" part only.
    }
  };

  const handleChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Handle Multi-select for Material Type
  const handleTypeChange = (e) => {
    const { value, checked } = e.target;
    let currentTypes = filters.type ? (Array.isArray(filters.type) ? filters.type : filters.type.split(',')) : [];
    currentTypes = currentTypes.filter(t => t); // Clean

    if (checked) {
      if (!currentTypes.includes(value)) currentTypes.push(value);
    } else {
      currentTypes = currentTypes.filter(t => t !== value);
    }
    setFilters(prev => ({ ...prev, type: currentTypes }));
  };

  const clearAllFilters = () => {
    setFilters({ name: '', type: [], minPrice: '', maxPrice: '', minWeight: '', maxWeight: '', category: '', purity: '' });
    setSearchTerm('');
  };

  const isTypeSelected = (value) => {
    if (!filters.type) return false;
    const currentTypes = Array.isArray(filters.type) ? filters.type : filters.type.split(',');
    return currentTypes.includes(value);
  };

  // Token Removal Helper
  const removeFilterToken = (key, value = null) => {
    const newFilters = { ...filters };
    if (key === 'type' && value) {
      let currentTypes = Array.isArray(newFilters.type) ? newFilters.type : newFilters.type.split(',');
      newFilters.type = currentTypes.filter(t => t !== value);
    } else {
      newFilters[key] = '';
    }
    setFilters(newFilters);
    if (key === 'name') setSearchTerm('');
  };

  return (
    <div className="modern-filter-container">
      <div className="filter-header-modern">
        <h3>Filters</h3>
        <button type="button" className="btn-link-sm" onClick={clearAllFilters}>Clear All</button>
      </div>

      {/* --- ACTIVE FILTER TOKENS (Chips) --- */}
      <div className="active-tokens-area">
        {filters.type && (Array.isArray(filters.type) ? filters.type : filters.type.split(',')).map(t => t && (
          <span key={t} className="filter-token token-gold" onClick={() => removeFilterToken('type', t)}>{t} ×</span>
        ))}
        {filters.category && (
          <span className="filter-token token-blue" onClick={() => removeFilterToken('category')}>{filters.category} ×</span>
        )}
        {filters.purity && (
          <span className="filter-token token-green" onClick={() => removeFilterToken('purity')}>{filters.purity} ×</span>
        )}
        {(filters.minPrice || filters.maxPrice) && (
          <span className="filter-token token-purple" onClick={() => { removeFilterToken('minPrice'); removeFilterToken('maxPrice'); }}>
            {filters.minPrice ? `₹${filters.minPrice}` : '0'} - {filters.maxPrice ? `₹${filters.maxPrice}` : '∞'} ×
          </span>
        )}
      </div>

      <div className="filter-group-modern">
        <label className="filter-label">Sort By</label>
        <select
          className="modern-input sort-select-bar"
          value={`${filters.sortBy || 'createdAt'}_${filters.order || 'desc'}`}
          onChange={(e) => {
            const val = e.target.value;
            const [sort, order] = val.includes('_') ? val.split('_') : ['createdAt', 'desc'];
            setFilters(prev => ({ ...prev, sortBy: sort, order: order }));
          }}
        >
          <option value="createdAt_desc">Latest Added</option>
          <option value="createdAt_asc">Oldest Added</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
      </div>

      <div className="filter-group-modern">
        <label className="filter-label">Smart Search</label>
        <div className="input-with-icon">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSmartSearch}
            placeholder="e.g. 'Gold Ring under 50k'"
            className="modern-input"
            title="Type 'Gold Ring under 50k' and press Enter"
          />
          <span className="search-icon-right">🔍</span>
        </div>
        <small className="hint-text">Try: "Silver Anklet under 5k"</small>
      </div>

      <div className="filter-group-modern">
        <label className="filter-label">Material</label>
        <div className="checkbox-group">
          {['gold', 'silver', 'diamond', 'custom'].map(m => (
            <label key={m} className="checkbox-item">
              <input type="checkbox" value={m} checked={isTypeSelected(m)} onChange={handleTypeChange} />
              <span style={{ textTransform: 'capitalize' }}>{m}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="filter-group-modern">
        <label className="filter-label">Category</label>
        <select
          name="category"
          value={filters.category || ''}
          onChange={handleChange}
          className="modern-input"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>

      <div className="filter-group-modern">
        <label className="filter-label">Price Range (₹)</label>
        <div className="range-inputs">
          <input name="minPrice" value={filters.minPrice} onChange={handleChange} placeholder="Min" type="number" className="modern-input range-input" />
          <span className="range-separator">-</span>
          <input name="maxPrice" value={filters.maxPrice} onChange={handleChange} placeholder="Max" type="number" className="modern-input range-input" />
        </div>
      </div>

      {isAdmin && (
        <div className="filter-group-modern">
          <label className="checkbox-item" style={{ marginTop: '10px' }}>
            <input
              type="checkbox"
              name="showHidden"
              checked={filters.showHidden || false}
              onChange={(e) => setFilters(prev => ({ ...prev, showHidden: e.target.checked }))}
            />
            <span style={{ fontWeight: 600, color: '#444' }}>Show Hidden Items</span>
          </label>
        </div>
      )}
    </div>
  );
};

export default JewelleryFilterBar;